
import React from "react";

const QUESTION_TYPES = [
  { value: "", label: "All Question Types" },
  { value: "singleChoice", label: "Single Choice" },
  { value: "multipleChoice", label: "Multiple Choice" },
  { value: "statementBased", label: "Statement Based" },
  { value: "synonym", label: "Synonym" },
  { value: "antonym", label: "Antonym" },
  { value: "vocabulary", label: "Vocabulary" },
  { value: "fillInTheBlank", label: "Fill in the Blank" },
  { value: "errorDetection", label: "Error Detection" },
  { value: "sentenceImprovement", label: "Sentence Improvement" },
  { value: "phraseReplacement", label: "Phrase Replacement" },
  { value: "clozeTest", label: "Cloze Test" },
  { value: "paraJumble", label: "Para Jumble" },
  { value: "matchFollowing", label: "Match the Following" },
];

const DIFFICULTIES = [
  { value: "", label: "All Difficulties" },
  { value: "easy", label: "Easy" },
  { value: "medium", label: "Medium" },
  { value: "hard", label: "Hard" },
];

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const EnglishQuestionFilters = ({
  filters = {},
  onChange,
  onReset,
  subjects = [],
  chapters = [],
  groups = [],
  showSubject = true,
  showChapter = true,
  showGroup = true,
  showQuestionType = true,
  showDifficulty = true,
  showStatus = true,
  showSearch = true,
}) => {
  const {
    search = "",
    subject = "",
    chapter = "",
    group = "",
    questionType = "",
    difficulty = "",
    status = "",
  } = filters;

  const updateFilter = (field, value) => {
    onChange?.({
      ...filters,
      [field]: value,
    });
  };

  const handleSubjectChange = (value) => {
    onChange?.({
      ...filters,
      subject: value,
      chapter: "",
      group: "",
    });
  };

  const handleChapterChange = (value) => {
    onChange?.({
      ...filters,
      chapter: value,
      group: "",
    });
  };

  const hasActiveFilters =
    search ||
    subject ||
    chapter ||
    group ||
    questionType ||
    difficulty ||
    status;

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
      {/* Header */}
      <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">
            Filter Questions
          </h3>

          <p className="mt-1 text-xs text-gray-500">
            Filter English questions by subject, chapter, group, type,
            difficulty, or status.
          </p>
        </div>

        {hasActiveFilters && (
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
          >
            Reset Filters
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {/* Search */}
        {showSearch && (
          <div className="sm:col-span-2 lg:col-span-3 xl:col-span-4">
            <label
              htmlFor="english-question-search"
              className="mb-1.5 block text-sm font-medium text-gray-700"
            >
              Search
            </label>

            <div className="relative">
              <svg
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m21 21-4.35-4.35m1.35-5.65a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z"
                />
              </svg>

              <input
                id="english-question-search"
                type="text"
                value={search}
                onChange={(e) =>
                  updateFilter("search", e.target.value)
                }
                placeholder="Search question text..."
                className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </div>
          </div>
        )}

        {/* Subject */}
        {showSubject && (
          <FilterSelect
            id="english-question-subject"
            label="Subject"
            value={subject}
            options={subjects}
            placeholder="All Subjects"
            optionValue="value"
            optionLabel="label"
            onChange={handleSubjectChange}
          />
        )}

        {/* Chapter */}
        {showChapter && (
          <FilterSelect
            id="english-question-chapter"
            label="Chapter"
            value={chapter}
            options={chapters}
            placeholder="All Chapters"
            optionValue="value"
            optionLabel="label"
            onChange={handleChapterChange}
            disabled={!subject && chapters.length === 0}
          />
        )}

        {/* Group */}
        {showGroup && (
          <FilterSelect
            id="english-question-group"
            label="Question Group"
            value={group}
            options={groups}
            placeholder="All Groups"
            optionValue="value"
            optionLabel="label"
            onChange={(value) => updateFilter("group", value)}
          />
        )}

        {/* Question Type */}
        {showQuestionType && (
          <FilterSelect
            id="english-question-type"
            label="Question Type"
            value={questionType}
            options={QUESTION_TYPES}
            placeholder="All Question Types"
            optionValue="value"
            optionLabel="label"
            onChange={(value) =>
              updateFilter("questionType", value)
            }
          />
        )}

        {/* Difficulty */}
        {showDifficulty && (
          <FilterSelect
            id="english-question-difficulty"
            label="Difficulty"
            value={difficulty}
            options={DIFFICULTIES}
            placeholder="All Difficulties"
            optionValue="value"
            optionLabel="label"
            onChange={(value) =>
              updateFilter("difficulty", value)
            }
          />
        )}

        {/* Status */}
        {showStatus && (
          <FilterSelect
            id="english-question-status"
            label="Status"
            value={status}
            options={STATUS_OPTIONS}
            placeholder="All Statuses"
            optionValue="value"
            optionLabel="label"
            onChange={(value) => updateFilter("status", value)}
          />
        )}
      </div>

      {/* Active filter summary */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 bg-gray-50 px-4 py-3">
          <span className="mr-1 text-xs font-medium text-gray-500">
            Active filters:
          </span>

          {search && (
            <FilterBadge
              label={`Search: ${search}`}
              onRemove={() => updateFilter("search", "")}
            />
          )}

          {subject && (
            <FilterBadge
              label={`Subject: ${getOptionLabel(
                subjects,
                subject,
                "value",
                "label"
              )}`}
              onRemove={() => handleSubjectChange("")}
            />
          )}

          {chapter && (
            <FilterBadge
              label={`Chapter: ${getOptionLabel(
                chapters,
                chapter,
                "value",
                "label"
              )}`}
              onRemove={() => handleChapterChange("")}
            />
          )}

          {group && (
            <FilterBadge
              label={`Group: ${getOptionLabel(
                groups,
                group,
                "value",
                "label"
              )}`}
              onRemove={() => updateFilter("group", "")}
            />
          )}

          {questionType && (
            <FilterBadge
              label={`Type: ${getOptionLabel(
                QUESTION_TYPES,
                questionType,
                "value",
                "label"
              )}`}
              onRemove={() =>
                updateFilter("questionType", "")
              }
            />
          )}

          {difficulty && (
            <FilterBadge
              label={`Difficulty: ${getOptionLabel(
                DIFFICULTIES,
                difficulty,
                "value",
                "label"
              )}`}
              onRemove={() =>
                updateFilter("difficulty", "")
              }
            />
          )}

          {status && (
            <FilterBadge
              label={`Status: ${getOptionLabel(
                STATUS_OPTIONS,
                status,
                "value",
                "label"
              )}`}
              onRemove={() => updateFilter("status", "")}
            />
          )}
        </div>
      )}
    </div>
  );
};

/* =========================================================
   Reusable Select
   ========================================================= */

const FilterSelect = ({
  id,
  label,
  value,
  options = [],
  placeholder,
  optionValue = "value",
  optionLabel = "label",
  onChange,
  disabled = false,
}) => {
  return (
    <div>
      <label
        htmlFor={id}
        className="mb-1.5 block text-sm font-medium text-gray-700"
      >
        {label}
      </label>

      <select
        id={id}
        value={value || ""}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.value)}
        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
      >
        <option value="">{placeholder}</option>

        {options.map((option) => {
          const optionVal =
            typeof option === "object"
              ? option[optionValue]
              : option;

          const optionText =
            typeof option === "object"
              ? option[optionLabel]
              : option;

          if (!optionVal) {
            return null;
          }

          return (
            <option
              key={String(optionVal)}
              value={optionVal}
            >
              {optionText}
            </option>
          );
        })}
      </select>
    </div>
  );
};

/* =========================================================
   Active Filter Badge
   ========================================================= */

const FilterBadge = ({ label, onRemove }) => {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
      <span className="max-w-[220px] truncate">{label}</span>

      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label}`}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full text-blue-500 transition hover:bg-blue-100 hover:text-blue-800 focus:outline-none"
      >
        <svg
          className="h-3 w-3"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6 6l12 12M18 6 6 18"
          />
        </svg>
      </button>
    </span>
  );
};

/* =========================================================
   Helpers
   ========================================================= */

const getOptionLabel = (
  options = [],
  value,
  valueKey = "value",
  labelKey = "label"
) => {
  const option = options.find((item) => {
    if (typeof item === "object") {
      return String(item[valueKey]) === String(value);
    }

    return String(item) === String(value);
  });

  if (!option) {
    return value;
  }

  return typeof option === "object"
    ? option[labelKey]
    : option;
};

export default EnglishQuestionFilters;

